/**
 * Name mappings between our display names, ISO codes, and Natural Earth atlas names.
 *
 * The world-atlas TopoJSON data uses Natural Earth naming conventions, which differ
 * from both ISO standard names and common English names. This module provides
 * bidirectional mappings to handle these differences.
 */

import type { CountryCode } from './types';

/**
 * Maps Natural Earth (TopoJSON) country names to ISO 3166-1 alpha-2 codes.
 * This is used when rendering the world map to look up country ratings.
 */
export const atlasNameToCode: Record<string, CountryCode> = {
  // North America
  'United States of America': 'US',
  'Canada': 'CA',
  'Mexico': 'MX',
  'Greenland': 'GL',
  'Bahamas': 'BS',
  'Cuba': 'CU',
  'Haiti': 'HT',
  'Dominican Rep.': 'DO',
  'Jamaica': 'JM',
  'Belize': 'BZ',
  'Guatemala': 'GT',
  'Honduras': 'HN',
  'El Salvador': 'SV',
  'Nicaragua': 'NI',
  'Costa Rica': 'CR',
  'Panama': 'PA',
  'Trinidad and Tobago': 'TT',
  'Puerto Rico': 'PR',
  'Barbados': 'BB',
  'Saint Lucia': 'LC',
  'Grenada': 'GD',
  'Saint Vincent and the Grenadines': 'VC',
  'Antigua and Barb.': 'AG',
  'Dominica': 'DM',
  'Saint Kitts and Nevis': 'KN',

  // South America
  'Brazil': 'BR',
  'Argentina': 'AR',
  'Chile': 'CL',
  'Colombia': 'CO',
  'Peru': 'PE',
  'Venezuela': 'VE',
  'Ecuador': 'EC',
  'Bolivia': 'BO',
  'Paraguay': 'PY',
  'Uruguay': 'UY',
  'Guyana': 'GY',
  'Suriname': 'SR',
  'French Guiana': 'GF',
  'Falkland Is.': 'FK',

  // Europe
  'United Kingdom': 'GB',
  'France': 'FR',
  'Germany': 'DE',
  'Italy': 'IT',
  'Spain': 'ES',
  'Netherlands': 'NL',
  'Sweden': 'SE',
  'Norway': 'NO',
  'Denmark': 'DK',
  'Switzerland': 'CH',
  'Austria': 'AT',
  'Belgium': 'BE',
  'Portugal': 'PT',
  'Greece': 'GR',
  'Poland': 'PL',
  'Ireland': 'IE',
  'Iceland': 'IS',
  'Finland': 'FI',
  'Russia': 'RU',
  'Ukraine': 'UA',
  'Belarus': 'BY',
  'Estonia': 'EE',
  'Latvia': 'LV',
  'Lithuania': 'LT',
  'Moldova': 'MD',
  'Romania': 'RO',
  'Bulgaria': 'BG',
  'Hungary': 'HU',
  'Slovakia': 'SK',
  'Czechia': 'CZ',
  'Slovenia': 'SI',
  'Croatia': 'HR',
  'Bosnia and Herz.': 'BA',
  'Serbia': 'RS',
  'Montenegro': 'ME',
  'Kosovo': 'XK',
  'Albania': 'AL',
  'North Macedonia': 'MK',
  'Luxembourg': 'LU',
  'Malta': 'MT',
  'Cyprus': 'CY',
  'Andorra': 'AD',
  'San Marino': 'SM',
  'Vatican': 'VA',
  'Monaco': 'MC',
  'Liechtenstein': 'LI',

  // Asia
  'Japan': 'JP',
  'South Korea': 'KR',
  'China': 'CN',
  'India': 'IN',
  'Indonesia': 'ID',
  'Pakistan': 'PK',
  'Bangladesh': 'BD',
  'Philippines': 'PH',
  'Vietnam': 'VN',
  'Turkey': 'TR',
  'Iran': 'IR',
  'Thailand': 'TH',
  'Myanmar': 'MM',
  'Iraq': 'IQ',
  'Afghanistan': 'AF',
  'Saudi Arabia': 'SA',
  'Uzbekistan': 'UZ',
  'Malaysia': 'MY',
  'Yemen': 'YE',
  'Nepal': 'NP',
  'North Korea': 'KP',
  'Sri Lanka': 'LK',
  'Kazakhstan': 'KZ',
  'Syria': 'SY',
  'Cambodia': 'KH',
  'Jordan': 'JO',
  'Azerbaijan': 'AZ',
  'United Arab Emirates': 'AE',
  'Tajikistan': 'TJ',
  'Israel': 'IL',
  'Laos': 'LA',
  'Lebanon': 'LB',
  'Kyrgyzstan': 'KG',
  'Turkmenistan': 'TM',
  'Singapore': 'SG',
  'Oman': 'OM',
  'State of Palestine': 'PS',
  'Palestine': 'PS',
  'Kuwait': 'KW',
  'Georgia': 'GE',
  'Mongolia': 'MN',
  'Armenia': 'AM',
  'Qatar': 'QA',
  'Bahrain': 'BH',
  'Timor-Leste': 'TL',
  'Bhutan': 'BT',
  'Brunei': 'BN',
  'Taiwan': 'TW',
  'Maldives': 'MV',

  // Africa
  'Nigeria': 'NG',
  'Ethiopia': 'ET',
  'Egypt': 'EG',
  'Dem. Rep. Congo': 'CD',
  'Tanzania': 'TZ',
  'South Africa': 'ZA',
  'Kenya': 'KE',
  'Uganda': 'UG',
  'Algeria': 'DZ',
  'Sudan': 'SD',
  'Morocco': 'MA',
  'Angola': 'AO',
  'Mozambique': 'MZ',
  'Ghana': 'GH',
  'Madagascar': 'MG',
  'Cameroon': 'CM',
  "Côte d'Ivoire": 'CI',
  'Niger': 'NE',
  'Burkina Faso': 'BF',
  'Mali': 'ML',
  'Malawi': 'MW',
  'Zambia': 'ZM',
  'Senegal': 'SN',
  'Chad': 'TD',
  'Somalia': 'SO',
  'Zimbabwe': 'ZW',
  'Guinea': 'GN',
  'Rwanda': 'RW',
  'Benin': 'BJ',
  'Burundi': 'BI',
  'Tunisia': 'TN',
  'South Sudan': 'SS',
  'Togo': 'TG',
  'Sierra Leone': 'SL',
  'Libya': 'LY',
  'Congo': 'CG',
  'Liberia': 'LR',
  'Central African Rep.': 'CF',
  'Mauritania': 'MR',
  'Eritrea': 'ER',
  'Namibia': 'NA',
  'Gambia': 'GM',
  'Botswana': 'BW',
  'Gabon': 'GA',
  'Lesotho': 'LS',
  'Guinea-Bissau': 'GW',
  'Eq. Guinea': 'GQ',
  'Mauritius': 'MU',
  'eSwatini': 'SZ',
  'Eswatini': 'SZ',
  'Djibouti': 'DJ',
  'Comoros': 'KM',
  'Cabo Verde': 'CV',
  'Cape Verde': 'CV',
  'São Tomé and Principe': 'ST',
  'Sao Tome and Principe': 'ST',
  'Seychelles': 'SC',
  'W. Sahara': 'EH',
  'Western Sahara': 'EH',
  'Somaliland': 'SO', // Disputed, map to Somalia

  // Disputed territories (in 10m atlas)
  'N. Cyprus': 'CY', // Northern Cyprus, map to Cyprus
  'Cyprus U.N. Buffer Zone': 'CY', // Map to Cyprus
  'Transnistria': 'MD', // Map to Moldova
  'S. Ossetia': 'GE', // South Ossetia, map to Georgia
  'Abkhazia': 'GE', // Map to Georgia

  // Oceania
  'Australia': 'AU',
  'Papua New Guinea': 'PG',
  'New Zealand': 'NZ',
  'Fiji': 'FJ',
  'Solomon Is.': 'SB',
  'Solomon Islands': 'SB',
  'Vanuatu': 'VU',
  'New Caledonia': 'NC',
  'Samoa': 'WS',
  'Tonga': 'TO',
  'Kiribati': 'KI',
  'Micronesia': 'FM',
  'Palau': 'PW',
  'Marshall Is.': 'MH',
  'Marshall Islands': 'MH',
  'Nauru': 'NR',
  'Tuvalu': 'TV',
  'Fr. Polynesia': 'PF',
  'French Polynesia': 'PF',
};

/**
 * Maps our display country names to Natural Earth atlas names.
 * Used when looking up a specific country in the TopoJSON data.
 *
 * Only includes entries where names differ - if not in this map,
 * the display name is assumed to match the atlas name.
 */
export const displayNameToAtlasName: Record<string, string> = {
  'United States': 'United States of America',
  'UK': 'United Kingdom',
  'Czech Republic': 'Czechia',
  'Democratic Republic of the Congo': 'Dem. Rep. Congo',
  'Republic of the Congo': 'Congo',
  'Bosnia and Herzegovina': 'Bosnia and Herz.',
  'North Macedonia': 'North Macedonia',
  'Dominican Republic': 'Dominican Rep.',
  'Central African Republic': 'Central African Rep.',
  'Equatorial Guinea': 'Eq. Guinea',
  'Solomon Islands': 'Solomon Is.',
  'Ivory Coast': "Côte d'Ivoire",
  'Swaziland': 'eSwatini',
  'East Timor': 'Timor-Leste',
  'Antigua and Barbuda': 'Antigua and Barb.',
  'Marshall Islands': 'Marshall Is.',
  'DR Congo': 'Dem. Rep. Congo',
  'Western Sahara': 'W. Sahara',
  'Cape Verde': 'Cabo Verde',
  'Vatican City': 'Vatican',
};

/**
 * Reverse mapping: ISO code to Natural Earth atlas name.
 * Built by inverting atlasNameToCode.
 */
export const codeToAtlasName: Record<CountryCode, string> = Object.fromEntries(
  Object.entries(atlasNameToCode).map(([name, code]) => [code, name])
);

/**
 * Get the Natural Earth atlas name for a country.
 * Tries display name mapping first, then falls back to the display name itself.
 */
export function getAtlasName(displayName: string): string {
  return displayNameToAtlasName[displayName] ?? displayName;
}

/**
 * Get the ISO code for a Natural Earth atlas name.
 */
export function getCodeFromAtlasName(atlasName: string): CountryCode | undefined {
  return atlasNameToCode[atlasName];
}
